/**
 * One status chip (STATE-001…004). Colour is never the only cue: the label is always text, and
 * warning and error also carry an icon. An absent or unknown status is "—", never an invented value.
 */
import { Chip, type ChipProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/theme';
import { statusStyle, type StatusDomain, type Tone } from './statusTones';

const COLOR: Record<Tone, ChipProps['color']> = {
  success: 'success',
  info: 'info',
  warning: 'warning',
  error: 'error',
  neutral: 'default',
  draft: 'default',
};

export function StatusChip({
  domain,
  status,
  size = 'small',
  values,
}: {
  domain: StatusDomain;
  status: string | null | undefined;
  size?: 'small' | 'medium';
  values?: Record<string, string | number>;
}) {
  const { t } = useTranslation();
  const entry = statusStyle(domain, status);
  if (!entry) return t('statements.unavailable');
  const iconName = entry.tone === 'warning' ? 'warning' : entry.tone === 'error' ? 'error' : null;
  return (
    <Chip
      size={size}
      color={COLOR[entry.tone]}
      variant={entry.tone === 'draft' ? 'outlined' : 'filled'}
      icon={iconName ? <Icon name={iconName} /> : undefined}
      label={values ? t(entry.labelKey, values) : t(entry.labelKey)}
    />
  );
}

/** The rate card / plan "Non-compliant" warning, the same wherever a card is shown. */
export function NoncompliantChip({ size = 'small' }: { size?: 'small' | 'medium' }) {
  return <StatusChip domain="rateCard" status="NONCOMPLIANT" size={size} />;
}
